import React, { useState } from "react";

const faqData = {
  en: {
    title: "Frequently Asked Questions",
    subtitle: "Everything you need to know about Vapor.",
    categories: [
      {
        name: "Getting Started",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        ),
        items: [
          {
            q: "What is Vapor?",
            a: "Vapor is a free, open-source, end-to-end encrypted messenger designed with privacy at its core. Unlike traditional messaging apps, Vapor does not require a phone number or email to sign up — you communicate using an anonymous Account ID. Messages are routed through a decentralized onion-routing network, ensuring no single entity can track your conversations.",
          },
          {
            q: "How do I create an account?",
            a: "Simply download Vapor, open the app, and tap \"Create Account.\" A unique Account ID will be generated for you automatically — no phone number, no email, no personal information required. Share your Account ID with friends so they can message you securely.",
          },
{
            q: "Is Vapor free to use?",
            a: "Yes, Vapor is completely free. There are no subscription fees, no premium tiers, and no ads. Vapor is funded by community donations and maintained by volunteers who believe privacy is a fundamental right.",
          },
        ],
      },
      {
        name: "Privacy & Security",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        ),
        items: [
          {
            q: "What data does Vapor collect?",
            a: "Vapor collects no personal data whatsoever. We don't store your IP address, contacts, metadata, or message content. Since we never have your data, there is nothing to leak, sell, or hand over to third parties.",
          },
          {
            q: "How does end-to-end encryption work in Vapor?",
            a: "Every message you send is encrypted on your device before it leaves, and can only be decrypted by the intended recipient. Not even Vapor's servers can read your messages. We use the Signal Protocol for one-to-one chats, ensuring forward secrecy and robust cryptographic protection.",
          },
          {
            q: "What is onion routing and why does Vapor use it?",
            a: "Onion routing sends your messages through multiple nodes in a decentralized network, with each layer of encryption peeled off at each hop. This means no single node knows both the sender and recipient. It protects your IP address and makes traffic analysis extremely difficult.",
          },
          {
            q: "Can Vapor be used for anonymous communication?",
            a: "Yes. Since Vapor requires no personal information to register and uses onion routing to obscure network metadata, it provides a high level of anonymity. Your Account ID is not linked to any real-world identity.",
          },
          {
            q: "Has Vapor been independently audited?",
            a: "Vapor is fully open source, meaning anyone can inspect, audit, and verify the code at any time. Our codebase is publicly available on GitHub. We encourage security researchers to review our code and report any findings through our responsible disclosure program.",
          },
        ],
      },
      {
        name: "Features",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        ),
        items: [
          {
            q: "Does Vapor support group chats?",
            a: "Yes! Vapor supports secure group chats with 100+ members. All group messages are end-to-end encrypted, and you can manage group settings, invite members via Account ID, and enjoy the same level of privacy as one-to-one conversations.",
          },
          {
            q: "Can I send files, images, and voice messages?",
            a: "Absolutely. Vapor supports sending images, videos, documents, voice messages, and other file types — all protected by end-to-end encryption. Attachments are encrypted before upload and can only be decrypted by the conversation participants.",
          },
          {
            q: "What are disappearing messages?",
            a: "Disappearing messages automatically delete themselves after a set period (from 5 seconds to 1 week). Once the timer expires, the message is removed from both the sender's and recipient's devices, adding an extra layer of privacy.",
          },
          {
            q: "Does Vapor support voice or video calls?",
            a: "Voice calls are supported with end-to-end encryption routed through the decentralized network. Video calling is currently in development and will be available in a future update.",
          },
        ],
      },
      {
        name: "Account & Data",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ),
        items: [
          {
            q: "What happens if I lose my device?",
            a: "During account creation, Vapor provides a recovery phrase. As long as you have saved this phrase, you can restore your Account ID and settings on a new device. Without the recovery phrase, your account cannot be recovered — this is by design to ensure maximum security.",
          },
          {
            q: "Can I use Vapor on multiple devices?",
            a: "Yes, Vapor supports multi-device usage. Link your devices by scanning a QR code or entering your recovery phrase. Messages are synced across devices with end-to-end encryption maintained on each one.",
          },
          {
            q: "How do I delete my account?",
            a: "You can delete your account from the app settings at any time. Since Vapor stores no personal data on its servers, deletion is immediate and complete. All local message history is wiped from your device.",
          },
        ],
      },
      {
        name: "Open Source & Community",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6"/>
            <polyline points="8 6 2 12 8 18"/>
          </svg>
        ),
        items: [
          {
            q: "Is Vapor really open source?",
            a: "Yes, 100%. Vapor's entire codebase — including the client apps, server infrastructure, and cryptographic protocols — is publicly available on GitHub at github.com/Vapor-web3. We believe transparency is essential for trust.",
          },
          {
            q: "How can I contribute to Vapor?",
            a: "We welcome contributions! You can report bugs, submit pull requests, help with translations, improve documentation, or run a community node. Visit our GitHub repository to get started. Every contribution helps make Vapor better for everyone.",
          },
          {
            q: "How is Vapor funded?",
            a: "Vapor is funded entirely by community donations. We have no venture capital investors, no advertising revenue, and no corporate interests. This independence ensures that our only priority is protecting user privacy.",
          },
        ],
      },
    ],
  },
  zhTW: {
    title: "常見問題",
    subtitle: "關於 Vapor 您需要知道的一切。",
    categories: [
      {
        name: "快速上手",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        ),
        items: [
          {
            q: "Vapor 是什麼？",
            a: "Vapor 是一款免費、開放原始碼、端對端加密的通訊軟體，以隱私保護為核心設計理念。與傳統通訊軟體不同，Vapor 無需手機號碼或電子郵件即可註冊——您透過匿名的帳戶 ID 進行通訊。訊息透過去中心化的洋蔥路由網路傳送，確保沒有任何單一實體能追蹤您的對話。",
          },
          {
            q: "如何建立帳戶？",
            a: "下載 Vapor 後，開啟應用程式並點擊「建立帳戶」。系統會自動為您生成一組獨特的帳戶 ID——無需手機號碼、電子郵件或任何個人資訊。將您的帳戶 ID 分享給朋友，即可開始安全通訊。",
          },
{
            q: "Vapor 是免費的嗎？",
            a: "是的，Vapor 完全免費。沒有訂閱費用、沒有進階方案、沒有廣告。Vapor 由社群捐款資助，由堅信隱私是基本人權的志願者共同維護。",
          },
        ],
      },
      {
        name: "隱私與安全",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        ),
        items: [
          {
            q: "Vapor 會蒐集哪些資料？",
            a: "Vapor 不蒐集任何個人資料。我們不會儲存您的 IP 位址、聯絡人、後設資料或訊息內容。由於我們從未持有您的資料，因此不存在外洩、出售或移交給第三方的可能。",
          },
          {
            q: "Vapor 的端對端加密如何運作？",
            a: "您發送的每則訊息都會在裝置上加密後才送出，且只有預期的接收者才能解密。即使是 Vapor 的伺服器也無法讀取您的訊息。我們採用 Signal Protocol 進行一對一聊天，確保前向保密性和強健的密碼學保護。",
          },
          {
            q: "什麼是洋蔥路由？Vapor 為何使用它？",
            a: "洋蔥路由將您的訊息透過去中心化網路中的多個節點傳送，每一跳都會剝離一層加密。這意味著沒有任何單一節點同時知道發送者和接收者。它能保護您的 IP 位址，並使流量分析變得極為困難。",
          },
          {
            q: "Vapor 可以用於匿名通訊嗎？",
            a: "可以。由於 Vapor 註冊無需任何個人資訊，且使用洋蔥路由來隱藏網路後設資料，它提供了高度的匿名性。您的帳戶 ID 不與任何現實身份關聯。",
          },
          {
            q: "Vapor 是否經過獨立審計？",
            a: "Vapor 是完全開放原始碼的，任何人都可以隨時檢視、審計和驗證程式碼。我們的程式碼庫公開在 GitHub 上。我們鼓勵安全研究人員審查程式碼，並透過我們的負責任揭露計劃回報任何發現。",
          },
        ],
      },
      {
        name: "功能特色",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        ),
        items: [
          {
            q: "Vapor 支援群組聊天嗎？",
            a: "支援！Vapor 支援 100 人以上的安全群組聊天。所有群組訊息都經過端對端加密，您可以管理群組設定、透過帳戶 ID 邀請成員，享受與一對一對話相同等級的隱私保護。",
          },
          {
            q: "可以傳送檔案、圖片和語音訊息嗎？",
            a: "當然可以。Vapor 支援傳送圖片、影片、文件、語音訊息和其他檔案類型——全部受到端對端加密保護。附件在上傳前即已加密，只有對話參與者才能解密。",
          },
          {
            q: "什麼是限時訊息？",
            a: "限時訊息會在設定的時間後自動刪除（從 5 秒到 1 週不等）。計時器到期後，訊息會從發送者和接收者的裝置上同時移除，為隱私增添額外保護層。",
          },
          {
            q: "Vapor 支援語音或視訊通話嗎？",
            a: "語音通話已支援，透過去中心化網路進行端對端加密路由。視訊通話目前正在開發中，將在未來的更新中推出。",
          },
        ],
      },
      {
        name: "帳戶與資料",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ),
        items: [
          {
            q: "如果我遺失了裝置怎麼辦？",
            a: "建立帳戶時，Vapor 會提供一組恢復助記詞。只要您保存了這組助記詞，就能在新裝置上恢復您的帳戶 ID 和設定。若沒有恢復助記詞，帳戶將無法恢復——這是為了確保最高安全性的設計。",
          },
          {
            q: "我可以在多台裝置上使用 Vapor 嗎？",
            a: "可以，Vapor 支援多裝置使用。透過掃描 QR Code 或輸入恢復助記詞來連結裝置。訊息會在裝置間同步，每台裝置都維持端對端加密。",
          },
          {
            q: "如何刪除帳戶？",
            a: "您可以隨時在應用程式設定中刪除帳戶。由於 Vapor 不在伺服器上儲存任何個人資料，刪除操作是即時且徹底的。所有本機訊息記錄都會從裝置中清除。",
          },
        ],
      },
      {
        name: "開放原始碼與社群",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d46a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6"/>
            <polyline points="8 6 2 12 8 18"/>
          </svg>
        ),
        items: [
          {
            q: "Vapor 真的是開放原始碼嗎？",
            a: "是的，百分之百。Vapor 的整個程式碼庫——包括客戶端應用、伺服器基礎設施和密碼學協議——都公開在 GitHub 上（github.com/Vapor-web3）。我們相信透明度是建立信任的根本。",
          },
          {
            q: "如何為 Vapor 做出貢獻？",
            a: "我們歡迎各種貢獻！您可以回報錯誤、提交 Pull Request、協助翻譯、改善文件，或運行社群節點。請前往我們的 GitHub 儲存庫開始參與。每一份貢獻都能讓 Vapor 變得更好。",
          },
          {
            q: "Vapor 如何獲得資金支持？",
            a: "Vapor 完全由社群捐款資助。我們沒有風險投資、沒有廣告收入、沒有企業利益。這種獨立性確保我們唯一的優先事項就是保護用戶隱私。",
          },
        ],
      },
    ],
  },
};

// 單個可展開的 FAQ 項目
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq-item ${isOpen ? "faq-item-open" : ""}`} onClick={onClick}>
    <div className="faq-question">
      <span>{question}</span>
      <svg
        className="faq-chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const FAQ = ({ locale }) => {
  const key = locale === "zh-TW" ? "zhTW" : locale;
  const data = faqData[key] || faqData.en;
  // 用 "categoryIndex-itemIndex" 來追蹤當前展開的項目
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="legal-page faq-page">
      <div className="container">
        <div className="legal-header">
          <h1>{data.title}</h1>
          <p className="legal-updated">{data.subtitle}</p>
        </div>

        <div className="faq-categories">
          {data.categories.map((cat, ci) => (
            <div className="faq-category" key={ci}>
              <div className="faq-category-header">
                <span className="faq-category-icon">{cat.icon}</span>
                <h2>{cat.name}</h2>
              </div>
              <div className="faq-list">
                {cat.items.map((item, ii) => {
                  const id = `${ci}-${ii}`;
                  return (
                    <FAQItem
                      key={id}
                      question={item.q}
                      answer={item.a}
                      isOpen={openId === id}
                      onClick={() => toggle(id)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
